import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AccountInfo,
  AuthError,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  InteractionType
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { datadogLogs } from '@datadog/browser-logs';
import { AuthContext, AuthContextType } from './AuthContext';
import { keepTokenFresh } from './entraTokenRenewal';
import { useApiToken } from './useApiToken';

// Every redirect sign-in, new account or cached, ends in a redirect token success naming the account chosen.
const accountChosenBy = (message: EventMessage): AccountInfo | null => {
  const isRedirectToken =
    message.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && message.interactionType === InteractionType.Redirect;
  return isRedirectToken ? ((message.payload as AuthenticationResult | null)?.account ?? null) : null;
};

type FailedDuring = 'redirect' | 'session';

// The error code says what Entra refused (AADSTS50011 redirect URI, an unassigned user, declined consent); the token,
// and anything else in the error, stays out of the log.
const logSignInFailure = (during: FailedDuring, failure: unknown) =>
  datadogLogs.logger.warn('Entra sign-in failed', {
    during,
    errorCode: failure instanceof AuthError ? failure.errorCode : failure instanceof Error ? failure.name : undefined
  });

interface EntraAuthProviderProps {
  apiScope: string;
  children: ReactNode;
}

export const EntraAuthProvider = ({ apiScope, children }: EntraAuthProviderProps) => {
  const { instance, accounts, inProgress } = useMsal();
  const { token, installToken, dropToken } = useApiToken('entra');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPending, setIsPending] = useState(true);
  const [error, setError] = useState(false);
  // Several failures at once would each start a redirect; only the first gets MSAL's interaction lock.
  const redirectRequested = useRef(false);
  // A browser can cache several Microsoft accounts; the one signed in last is MSAL's active account.
  const [selectedAccountId, setSelectedAccountId] = useState(() => instance.getActiveAccount()?.homeAccountId ?? null);
  // useMsal returns a new accounts array on every context update, so the effect keys on the id.
  const homeAccountId =
    selectedAccountId && accounts.some((cached) => cached.homeAccountId === selectedAccountId)
      ? selectedAccountId
      : (accounts[0]?.homeAccountId ?? null);
  const tokenRequest = useMemo(() => ({ scopes: [apiScope] }), [apiScope]);

  // Any failure leaves no session, so the guard shows its retry screen instead of an app whose token is gone. The error
  // is set with the pending flag, never after it: a bare signed-out state makes the route guard redirect again.
  const failSignIn = useCallback(
    (during: FailedDuring, failure: unknown) => {
      logSignInFailure(during, failure);
      redirectRequested.current = false;
      setIsAuthenticated(false);
      dropToken();
      setError(true);
      setIsPending(false);
    },
    [dropToken, setError]
  );

  const startRedirect = useCallback(
    async (navigate: () => Promise<void>) => {
      if (redirectRequested.current) return;
      redirectRequested.current = true;
      setIsPending(true);
      try {
        await navigate();
      } catch (failure) {
        failSignIn('redirect', failure);
        throw failure;
      }
    },
    [failSignIn]
  );

  // The outcome of a redirect arrives here, after loginRedirect's promise already resolved with the page unloading.
  useEffect(() => {
    const callbackId = instance.addEventCallback(
      (message: EventMessage) => {
        const chosen = accountChosenBy(message);
        if (chosen) {
          instance.setActiveAccount(chosen);
          setSelectedAccountId(chosen.homeAccountId);
        }
        if (
          message.eventType === EventType.ACQUIRE_TOKEN_FAILURE &&
          message.interactionType === InteractionType.Redirect
        ) {
          failSignIn('redirect', message.error);
        }
      },
      [EventType.ACQUIRE_TOKEN_SUCCESS, EventType.ACQUIRE_TOKEN_FAILURE]
    );
    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance, failSignIn]);

  useEffect(() => {
    // The cached account outlives a declined consent; retrying here would bounce the user through Entra forever.
    if (error) return undefined;
    if (inProgress !== InteractionStatus.None) {
      setIsPending(true);
      return undefined;
    }

    const account = homeAccountId ? instance.getAccount({ homeAccountId }) : null;
    if (!account) {
      setIsAuthenticated(false);
      dropToken();
      setIsPending(false);
      return undefined;
    }

    setIsPending(true);
    instance.setActiveAccount(account);
    return keepTokenFresh(
      instance,
      { ...tokenRequest, account },
      {
        onToken: (accessToken) => {
          installToken(accessToken);
          setIsAuthenticated(true);
          setIsPending(false);
        },
        onInteractionRequired: () => {
          // startRedirect has already ended the session and logged a failure; nothing is left to handle here.
          startRedirect(() => instance.acquireTokenRedirect({ ...tokenRequest, account })).catch(() => undefined);
        },
        onFailure: (failure) => failSignIn('session', failure)
      }
    );
  }, [error, homeAccountId, inProgress, instance, tokenRequest, installToken, dropToken, startRedirect, failSignIn]);

  const signIn = useCallback(
    (returnTo: string) => {
      setError(false);
      return startRedirect(() =>
        instance.loginRedirect({ ...tokenRequest, redirectStartPage: `${window.location.origin}${returnTo}` })
      );
    },
    [instance, tokenRequest, startRedirect, setError]
  );

  const signOut = useCallback(() => instance.logoutRedirect(), [instance]);

  const value = useMemo<AuthContextType>(
    () => ({ provider: 'entra', authState: { isAuthenticated, isPending, error }, token, signIn, signOut }),
    [isAuthenticated, isPending, error, token, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
